/**
 * Every word the VOD screen says on its own behalf (`TV-3`).
 *
 * The titles, synopses and rail headings are the catalogue's; this is the chrome
 * around them — the eyebrow, the empty state, the word on the Play button — and
 * it is authored by hand for the reason `offersCopy` is: there are about twenty
 * strings, they are read at three metres by a guest with a remote, and a machine
 * translation that lands slightly wrong reads as a different product.
 *
 * Two locales, because those are the two Foyer ships hotels in today. An unknown
 * locale gets English rather than a blank screen — the same fail-visible
 * instinct the knob accessors follow.
 */

export type VodLocale = "sv" | "en";

export interface VodCopy {
  /** Heading, when the premise has NOT renamed the screen. */
  title: string;
  /** Small caps line above the heading. */
  eyebrow: string;
  /** Label of the first page tab — the home rails. */
  homeTab: string;
  /** A menu page that resolves to no rails for this room. */
  emptyPage: string;
  /** The library has not answered yet. */
  loading: string;
  /**
   * ⚠️ The library answered, and there is nothing in it.
   *
   * This is the COMMON case in production today — zero `packages` rows
   * means every rail resolves to nothing for every premise — so it is written as
   * a plain statement of fact with a next action, not as an error and certainly
   * not as a blank screen.
   */
  empty: string;
  /** The library could not be reached at all. Distinct from empty. */
  unreachable: string;
  /** What pressing OK on the unreachable state does. */
  retry: string;
  /** Resolving a stream, between the press and the first frame. */
  starting: string;
  play: string;
  episodes: string;
  seasonLabel: (n: number | undefined, index: number) => string;
  /** CONTENT-26: the supplier credit, when they wrote no credits text. */
  creditFrom: (name: string) => string;
  episodeLabel: (n: number | undefined, index: number) => string;
  /** "1 tim 48 min" / "1 hr 48 min". */
  duration: (seconds: number) => string;
  kind: Record<"movie" | "series" | "episode" | "other", string>;
  hint: {
    move: string;
    open: string;
    play: string;
    back: string;
    stop: string;
  };
}

/** "1 tim 48 min", from seconds. Whole minutes: a TV is not a stopwatch. */
function durationWords(seconds: number, hr: string, min: string): string {
  const total = Math.max(0, Math.round(seconds / 60));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} ${min}`;
  if (m === 0) return `${h} ${hr}`;
  return `${h} ${hr} ${m} ${min}`;
}

const SV: VodCopy = {
  title: "Film & TV",
  eyebrow: "STRÖMMAT",
  /** The first tab: everything the room's home screen draws. */
  homeTab: "Start",
  /** A page the operator put in the menu that resolves to nothing here. */
  emptyPage: "Den här sidan har inget att visa just nu.",
  loading: "Hämtar biblioteket…",
  empty:
    "Inget filmutbud är kopplat till det här hotellet ännu. Kontakta receptionen om du väntar dig innehåll här.",
  unreachable: "Biblioteket kunde inte hämtas. Kontrollera anslutningen.",
  retry: "Försök igen",
  starting: "Startar…",
  play: "Spela",
  episodes: "Avsnitt",
  seasonLabel: (n, i) => `Säsong ${n ?? i + 1}`,
  creditFrom: (name) => `Från ${name}`,
  episodeLabel: (n, i) => `Avsnitt ${n ?? i + 1}`,
  duration: (s) => durationWords(s, "tim", "min"),
  kind: {
    movie: "Film",
    series: "Serie",
    episode: "Avsnitt",
    other: "Titel",
  },
  hint: {
    move: "Bläddra",
    open: "Öppna",
    play: "Spela",
    back: "Tillbaka",
    stop: "Avsluta",
  },
};

const EN: VodCopy = {
  title: "Movies & TV",
  eyebrow: "STREAMING",
  homeTab: "Start",
  emptyPage: "This page has nothing to show right now.",
  loading: "Loading the library…",
  empty:
    "No film library is connected to this hotel yet. Contact reception if you expected content here.",
  unreachable: "The library could not be loaded. Check the connection.",
  retry: "Try again",
  starting: "Starting…",
  play: "Play",
  episodes: "Episodes",
  seasonLabel: (n, i) => `Season ${n ?? i + 1}`,
  creditFrom: (name) => `From ${name}`,
  episodeLabel: (n, i) => `Episode ${n ?? i + 1}`,
  duration: (s) => durationWords(s, "hr", "min"),
  kind: {
    movie: "Film",
    series: "Series",
    episode: "Episode",
    other: "Title",
  },
  hint: {
    move: "Browse",
    open: "Open",
    play: "Play",
    back: "Back",
    stop: "Stop",
  },
};

export function vodCopy(locale: string | undefined): VodCopy {
  return locale?.toLowerCase().startsWith("sv") ? SV : EN;
}
